import { Dispatch, SetStateAction, useMemo } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Row } from '../../../common/mobile-list/row';
import { useValidateUsersShallowStore } from '../../stores/validate-users-store';
import { usePreviewInfiniteScroll } from './hooks/use-preview-infinite-scroll';
import { RowCheckbox } from './row-checkbox';

type MobileListProps = {
  patients: GetPatientsBalanceResponse_PatientBalance[];
  selected: Record<string, boolean>;
  onSelectionChange: Dispatch<SetStateAction<Record<string, boolean>>>;
  useNewApi: boolean;
};
export const MobileList = ({ patients, selected, onSelectionChange, useNewApi }: MobileListProps) => {
  const { t } = useTranslation('payments');
  const { excluded, toggleExcluded } = useValidateUsersShallowStore('excluded', 'toggleExcluded');

  // New API flow - infinite scroll
  const {
    data: infiniteScrollData,
    isLoading: isInfiniteScrollLoading,
    isFetchingNextPage,
    hasNextPage,
    loadMore,
  } = usePreviewInfiniteScroll({
    enabled: useNewApi ?? false,
  });

  const selectedArr = useMemo(() => {
    return Object.entries(selected)
      .filter(([_, value]) => value)
      .map(([key, _]) => key);
  }, [selected]);

  const handleClick = (data: GetPatientsBalanceResponse_PatientBalance) => {
    onSelectionChange((prev) => {
      const newSelected = { ...prev };
      if (prev[data.patientId]) {
        delete newSelected[data.patientId];
      } else {
        newSelected[data.patientId] = true;
      }
      return newSelected;
    });
  };

  const listData = useNewApi ? infiniteScrollData : patients;
  const loading = useNewApi ? isInfiniteScrollLoading : undefined;
  const hasNext = useNewApi ? hasNextPage : false;
  const loadMoreFn = useNewApi ? loadMore : undefined;
  const isFetchingMore = useNewApi ? isFetchingNextPage : false;

  return (
    <PaginatedList
      height='100%'
      listData={listData}
      renderListItem={({ index, listItem }) => (
        <Row
          index={index}
          selected={selectedArr}
          data={listItem}
          onClick={handleClick}
          CustomSelectCheckbox={
            useNewApi
              ? () => (
                  <RowCheckbox
                    isSelected={!!listItem.phoneNumber && !excluded.includes(listItem.patientId)}
                    onChange={() => toggleExcluded(listItem.patientId)}
                    isDisabled={!listItem.phoneNumber}
                  />
                )
              : undefined
          }
        />
      )}
      hasNextPage={hasNext}
      endOfListText={`🎉 ${t('You have reached the end')}`}
      loadMore={loadMoreFn}
      isLoading={loading}
      isFetchingNextPage={isFetchingMore}
    />
  );
};
